//----------------------------------------------------
// src/context/UserContext.jsx — Auth + RBAC context
//----------------------------------------------------
import { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {
  AUTH_LOGOUT_SYNC_KEY,
  AUTH_LOGOUT_EVENT,
  clearAuthStorage,
  getAccessToken,
  setAccessToken,
  setRefreshToken,
} from "../utils/authStorage";
import { API_BASE_URL } from "../config/api";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function syncUserFromStoredToken() {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const decoded = jwtDecode(token);
      setUser(decoded);
    } catch (err) {
      console.error("JWT decode fout:", err);
      clearAuthStorage();
      setUser(null);
    }
  }

  // Token inlezen bij paginaverversing
  useEffect(() => {
    syncUserFromStoredToken();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleForcedLogout() {
      setUser(null);
    }

    window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    return () => {
      window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    };
  }, []);

  useEffect(() => {
    function handleStorage(event) {
      if (event?.storageArea !== window.localStorage) return;
      const key = String(event?.key || "");
      if (
        key &&
        key !== "token" &&
        key !== "accessToken" &&
        key !== "refreshToken" &&
        key !== AUTH_LOGOUT_SYNC_KEY
      ) {
        return;
      }
      syncUserFromStoredToken();
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login → token opslaan + user zetten
  const login = (token, refreshToken) => {
    setAccessToken(token);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    const decoded = jwtDecode(token);
    setUser(decoded);
  };

  // Logout → token wissen
  const logout = () => {
    const token = getAccessToken();
    const apiUrl = API_BASE_URL;
    if (token) {
      fetch(`${apiUrl}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).catch(() => {
        // ignore logout sync failures
      });
    }
    clearAuthStorage();
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
